class ProductCardTooltip extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    // The tooltip text is re-parented to <body>, so it has to be cleaned up by
    // hand when the host element goes away (hover-overlay clones, section renders).
    this.tooltipText?.remove();
  }

  init() {
    if (this._initialized) return;

    const trigger = this.querySelector('.ne-product-card-disclosure-trigger');
    const tooltipText = this.querySelector('.ne-product-card-disclosure-text');

    if (!trigger || !tooltipText) return;

    this._initialized = true;
    this.trigger = trigger;
    this.tooltipText = tooltipText;

    document.body.appendChild(tooltipText);

    trigger.addEventListener('mouseenter', () => this.showTooltip());
    trigger.addEventListener('mouseleave', () => this.hideTooltip());
    trigger.addEventListener('focus', () => this.showTooltip());
    trigger.addEventListener('blur', () => this.hideTooltip());

    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleTooltip();
    });

    trigger.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        this.hideTooltip();
      }
    });

    if (!ProductCardTooltip._docListenerAdded) {
      document.addEventListener('click', (e) => {
        if (!e.target.closest('product-card-tooltip, .ne-product-card-disclosure-text')) {
          ProductCardTooltip.hideAll();
        }
      });
      // Focusing a trigger scrolls it into view, so hiding everything on scroll
      // would close the tooltip a keyboard user just opened. Keep the focused
      // one open and re-anchor it instead.
      const reflow = () => {
        const focused = document.activeElement?.closest('product-card-tooltip');
        ProductCardTooltip.hideAll(focused);
        focused?.positionTooltip();
      };
      window.addEventListener('scroll', reflow, true);
      window.addEventListener('resize', reflow);
      ProductCardTooltip._docListenerAdded = true;
    }
  }

  showTooltip() {
    ProductCardTooltip.hideAll(this);
    this.positionTooltip();

    this.tooltipText.style.opacity = '1';
    this.tooltipText.style.visibility = 'visible';
    this.tooltipText.style.pointerEvents = 'auto';
    this.trigger?.setAttribute('aria-expanded', 'true');
  }

  hideTooltip() {
    if (!this.tooltipText) return;
    this.tooltipText.style.opacity = '0';
    this.tooltipText.style.visibility = 'hidden';
    this.tooltipText.style.pointerEvents = 'none';
    this.trigger?.setAttribute('aria-expanded', 'false');
  }

  toggleTooltip() {
    const isVisible = this.tooltipText.style.opacity === '1';
    if (isVisible) {
      this.hideTooltip();
    } else {
      this.showTooltip();
    }
  }

  static hideAll(except) {
    document.querySelectorAll('product-card-tooltip').forEach((host) => {
      if (host !== except) host.hideTooltip();
    });
  }

  positionTooltip() {
    const tooltipText = this.tooltipText;
    const triggerRect = this.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const isMobile = viewportWidth < 768;
    const maxWidth = isMobile ? 200 : 278;
    const padding = 16;
    const gap = 8;

    tooltipText.style.left = '0px';
    tooltipText.style.top = '0px';
    tooltipText.style.transform = 'none';
    tooltipText.style.maxWidth = `${maxWidth}px`;

    const tooltipRect = tooltipText.getBoundingClientRect();
    const tooltipWidth = tooltipRect.width;
    const tooltipHeight = tooltipRect.height;

    const triggerCenter = triggerRect.left + triggerRect.width / 2;
    let left = triggerCenter - tooltipWidth / 2;

    if (left < padding) {
      left = padding;
    } else if (left + tooltipWidth > viewportWidth - padding) {
      left = viewportWidth - tooltipWidth - padding;
    }

    let top = triggerRect.top - tooltipHeight - gap;
    if (top < padding) {
      top = triggerRect.bottom + gap;
    }

    tooltipText.style.left = `${left}px`;
    tooltipText.style.top = `${top}px`;
  }
}

if (customElements.get('product-card-tooltip') === undefined) {
  customElements.define('product-card-tooltip', ProductCardTooltip);
}
